import { useEffect, useRef, useState } from "react";

/**
 * RollingNumber — premium slot-machine number reel (parent-controlled).
 *
 * Props:
 *   value:       string | null   -> the target 2-digit result "00".."99", or null
 *   isSpinning:  boolean         -> parent decides whether the reel is currently spinning
 *   testid:      string          -> data-testid for the reel window
 *
 * Behavior:
 *   - When isSpinning=true, the reel spins continuously (CSS animation).
 *   - When isSpinning transitions to false, the reel eases smoothly onto `value`
 *     with a decelerating ease-out (like a real casino reel).
 *   - When isSpinning=false and no value, shows "--" (placeholder).
 *   - Never displays a random number as the final settled state — always the
 *     exact value provided by the parent (backend result).
 */

const DIGITS = Array.from({ length: 100 }, (_, i) => String(i).padStart(2, "0"));

export default function RollingNumber({ value, isSpinning, testid }) {
  // Local phase: "spinning" | "settling" | "settled" | "empty"
  const [phase, setPhase] = useState(() => {
    if (isSpinning) return "spinning";
    if (value != null && value !== "" && value !== "--") return "settled";
    return "empty";
  });
  const [settledValue, setSettledValue] = useState(value || null);
  const glowRef = useRef(null);
  const prevSpinningRef = useRef(isSpinning);

  useEffect(() => {
    const wasSpinning = prevSpinningRef.current;
    prevSpinningRef.current = isSpinning;

    if (isSpinning) {
      // Enter spinning phase regardless of previous state
      setPhase("spinning");
      return;
    }

    // isSpinning=false. Decide how to settle.
    if (value == null || value === "" || value === "--") {
      setPhase("empty");
      setSettledValue(null);
      return;
    }

    if (wasSpinning) {
      // Was spinning, now stopping onto `value` -> smooth ease-out settle
      setSettledValue(value);
      setPhase("settling");
      const t = setTimeout(() => {
        setPhase("settled");
        // Gold flash burst on final stop
        if (glowRef.current) {
          glowRef.current.classList.remove("gold-flash");
          // Reflow to restart animation
          // eslint-disable-next-line no-unused-expressions
          void glowRef.current.offsetWidth;
          glowRef.current.classList.add("gold-flash");
        }
      }, 1400); // matches settling transition duration
      return () => clearTimeout(t);
    }

    // Not spinning and not from spin -> just show value
    setSettledValue(value);
    setPhase("settled");
  }, [isSpinning, value]);

  const idx = settledValue ? parseInt(settledValue, 10) : 0;

  // Compute strip transform for each phase
  let stripStyle = {};
  if (phase === "settling") {
    stripStyle = {
      transform: `translateY(-${idx}em)`,
      transition: "transform 1400ms cubic-bezier(0.22, 0.61, 0.36, 1)",
    };
  } else if (phase === "settled") {
    stripStyle = {
      transform: `translateY(-${idx}em)`,
      transition: "none",
    };
  } else if (phase === "empty") {
    stripStyle = {
      transform: "translateY(0)",
      transition: "none",
    };
  }
  // phase === "spinning" uses CSS keyframe animation, no inline style needed.

  const showPlaceholder = phase === "empty";

  return (
    <div className="reel-window" ref={glowRef} data-testid={testid}>
      {showPlaceholder ? (
        <div className="reel-cell reel-cell--placeholder">--</div>
      ) : (
        <div
          className={`reel-strip ${phase === "spinning" ? "reel-strip--spin" : ""}`}
          style={stripStyle}
        >
          {DIGITS.map((d) => (
            <div key={d} className="reel-cell">{d}</div>
          ))}
          {/* Duplicate for seamless CSS loop */}
          {DIGITS.map((d) => (
            <div key={`b-${d}`} className="reel-cell">{d}</div>
          ))}
        </div>
      )}
    </div>
  );
}
